import React, { useState, useRef, useEffect } from 'react';
import {
  getAppMediaResources,
  saveAppMediaResources,
  deployMediaAsset,
  resetMediaResourcesToDefaults,
  AppMediaResources,
  MediaAssetItem,
  MEDIA_CHANGE_EVENT,
} from '../../utils/mediaStorage';
import {
  getContactConfig,
  saveContactConfig,
  KingdomContactConfig,
  CONTACT_CONFIG_CHANGE_EVENT,
} from '../../utils/contactConfig';

interface AdminMultimediaManagerProps {
  adminEmail?: string;
  adminName?: string;
}

export const AdminMultimediaManager: React.FC<AdminMultimediaManagerProps> = ({
  adminEmail = 'erueuzairue@gmail.com',
  adminName = 'Ikpemhi Oshioke',
}) => {
  const [media, setMedia] = useState<AppMediaResources>(() => getAppMediaResources());
  const [contactConfig, setContactConfig] = useState<KingdomContactConfig>(() => getContactConfig());
  const [whatsappInput, setWhatsappInput] = useState<string>(() => getContactConfig().activeWhatsappNumber);
  const [toastMessage, setToastMessage] = useState<string | null>(null);

  const [videoTitleInput, setVideoTitleInput] = useState('');
  const [videoDescInput, setVideoDescInput] = useState('');
  const [videoUrlInput, setVideoUrlInput] = useState('');
  const [videoCategoryInput, setVideoCategoryInput] = useState('Keynote');
  const [photoTitleInput, setPhotoTitleInput] = useState('');
  const [photoCategoryInput, setPhotoCategoryInput] = useState('Culture');

  const logoInputRef = useRef<HTMLInputElement>(null);
  const secondaryLogoInputRef = useRef<HTMLInputElement>(null);
  const localVideoInputRef = useRef<HTMLInputElement>(null);
  const heroBannerInputRef = useRef<HTMLInputElement>(null);
  const portraitInputRef = useRef<HTMLInputElement>(null);
  const galleryPhotoInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const refresh = () => setMedia(getAppMediaResources());
    const refreshContact = () => {
      const cfg = getContactConfig();
      setContactConfig(cfg);
      setWhatsappInput(cfg.activeWhatsappNumber);
    };
    window.addEventListener(MEDIA_CHANGE_EVENT, refresh);
    window.addEventListener(CONTACT_CONFIG_CHANGE_EVENT, refreshContact);
    return () => {
      window.removeEventListener(MEDIA_CHANGE_EVENT, refresh);
      window.removeEventListener(CONTACT_CONFIG_CHANGE_EVENT, refreshContact);
    };
  }, []);

  const showToast = (msg: string) => {
    setToastMessage(msg);
    setTimeout(() => setToastMessage(null), 3500);
  };

  // Helper for FileReader Data URL convert
  const processFileUpload = (
    file: File,
    onSuccess: (dataUrl: string) => void,
    maxMb: number = 25
  ) => {
    if (file.size > maxMb * 1024 * 1024) {
      alert(`⚠️ File size exceeds ${maxMb}MB limit. Please select a smaller file.`);
      return;
    }
    const reader = new FileReader();
    reader.onload = (e) => {
      const result = e.target?.result as string;
      onSuccess(result);
    };
    reader.readAsDataURL(file);
  };

  // One-click logo deployment
  const handleLogoUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      processFileUpload(file, (dataUrl) => {
        deployMediaAsset('customLogoUrl', dataUrl, `${adminName} (${adminEmail})`);
        showToast('⚡ Primary Royal Logo uploaded & deployed app-wide immediately!');
      }, 10);
    }
  };

  const handleSecondaryLogoUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      processFileUpload(file, (dataUrl) => {
        deployMediaAsset('secondaryLogoUrl', dataUrl, `${adminName} (${adminEmail})`);
        showToast('⚡ Secondary Co-Sponsor Crest uploaded & deployed app-wide!');
      }, 10);
    }
  };

  // One-click hero banner deployment
  const handleHeroBannerUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      processFileUpload(file, (dataUrl) => {
        deployMediaAsset('heroBannerUrl', dataUrl, `${adminName} (${adminEmail})`);
        showToast('⚡ Hero Background Banner uploaded & deployed app-wide!');
      }, 15);
    }
  };

  // One-click royal portrait deployment
  const handlePortraitUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      processFileUpload(file, (dataUrl) => {
        deployMediaAsset('royalPortraitUrl', dataUrl, `${adminName} (${adminEmail})`);
        showToast('⚡ Executive Administrator Portrait uploaded & deployed app-wide!');
      }, 15);
    }
  };

  // One-click local video file upload
  const handleLocalVideoUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      processFileUpload(
        file,
        (dataUrl) => {
          const title = videoTitleInput.trim() || file.name.replace(/\.[^/.]+$/, '');
          const newMedia = getAppMediaResources();
          newMedia.festivalVideoUrl = dataUrl;
          newMedia.festivalVideoTitle = title;
          newMedia.festivalVideoDescription =
            videoDescInput.trim() || 'Uploaded & Deployed directly from Administration Console.';
          newMedia.lastUpdatedBy = `${adminName} (${adminEmail})`;
          newMedia.lastUpdatedAt = new Date().toISOString();

          // Add to promo list
          const newVidItem: MediaAssetItem = {
            id: `vid-${Date.now()}`,
            title,
            url: dataUrl,
            type: 'video',
            category: videoCategoryInput,
            isLiveBroadcast: true,
            fileSizeBytes: file.size,
            uploadedBy: adminName,
            updatedAt: new Date().toISOString(),
          };
          newMedia.promoVideos = [newVidItem, ...newMedia.promoVideos];

          saveAppMediaResources(newMedia);
          showToast(`🎬 Local Video "${title}" uploaded & deployed app-wide immediately!`);
          setVideoTitleInput('');
          setVideoDescInput('');
        },
        50
      );
    }
  };

  // Deploy Video URL (YouTube, Vimeo, MP4 link)
  const handleDeployVideoUrl = (e: React.FormEvent) => {
    e.preventDefault();
    if (!videoUrlInput.trim()) {
      alert('Please provide a valid video stream URL or MP4 link.');
      return;
    }

    const title = videoTitleInput.trim() || 'Official Uzairue Kingdom Video Broadcast';
    const desc = videoDescInput.trim() || 'Streamed directly via Royal Media Channel.';

    const newMedia = getAppMediaResources();
    newMedia.festivalVideoUrl = videoUrlInput.trim();
    newMedia.festivalVideoTitle = title;
    newMedia.festivalVideoDescription = desc;
    newMedia.lastUpdatedBy = `${adminName} (${adminEmail})`;
    newMedia.lastUpdatedAt = new Date().toISOString();

    const newVidItem: MediaAssetItem = {
      id: `vid-${Date.now()}`,
      title,
      url: videoUrlInput.trim(),
      type: 'video',
      category: videoCategoryInput,
      isLiveBroadcast: true,
      uploadedBy: adminName,
      updatedAt: new Date().toISOString(),
    };
    newMedia.promoVideos = [newVidItem, ...newMedia.promoVideos];

    saveAppMediaResources(newMedia);
    showToast(`🎬 Video URL "${title}" deployed as active app-wide festival video!`);
    setVideoUrlInput('');
    setVideoTitleInput('');
    setVideoDescInput('');
  };

  // Gallery photo upload
  const handleGalleryPhotoUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      processFileUpload(file, (dataUrl) => {
        const title = photoTitleInput.trim() || file.name.replace(/\.[^/.]+$/, '');
        const newMedia = getAppMediaResources();

        const newItem: MediaAssetItem = {
          id: `img-${Date.now()}`,
          title,
          url: dataUrl,
          type: 'gallery',
          category: photoCategoryInput,
          fileSizeBytes: file.size,
          uploadedBy: adminName,
          updatedAt: new Date().toISOString(),
        };

        newMedia.galleryImages = [newItem, ...newMedia.galleryImages];
        newMedia.lastUpdatedBy = `${adminName} (${adminEmail})`;
        newMedia.lastUpdatedAt = new Date().toISOString();

        saveAppMediaResources(newMedia);
        showToast(`🖼️ Gallery Image "${title}" added to multimedia library!`);
        setPhotoTitleInput('');
      }, 15);
    }
  };

  const handleSetActiveVideo = (vid: MediaAssetItem) => {
    const newMedia = getAppMediaResources();
    newMedia.festivalVideoUrl = vid.url;
    newMedia.festivalVideoTitle = vid.title;
    newMedia.lastUpdatedBy = `${adminName} (${adminEmail})`;
    newMedia.lastUpdatedAt = new Date().toISOString();
    saveAppMediaResources(newMedia);
    showToast(`▶️ Set "${vid.title}" as active main app-wide video!`);
  };

  const handleDeleteGalleryImage = (imgId: string) => {
    const newMedia = getAppMediaResources();
    newMedia.galleryImages = newMedia.galleryImages.filter((g) => g.id !== imgId);
    saveAppMediaResources(newMedia);
    showToast('🗑️ Gallery image removed.');
  };

  const handleDeleteVideo = (vidId: string) => {
    const newMedia = getAppMediaResources();
    newMedia.promoVideos = newMedia.promoVideos.filter((v) => v.id !== vidId);
    saveAppMediaResources(newMedia);
    showToast('🗑️ Video asset removed.');
  };

  const handleResetAllMedia = () => {
    if (confirm('Are you sure you want to reset all multimedia assets to kingdom defaults?')) {
      resetMediaResourcesToDefaults();
      showToast('🔄 Multimedia resources restored to default state.');
    }
  };

  const handleSaveWhatsapp = (e: React.FormEvent) => {
    e.preventDefault();
    if (!whatsappInput.trim()) {
      alert('Please enter a valid WhatsApp number.');
      return;
    }
    const updated: KingdomContactConfig = {
      ...contactConfig,
      activeWhatsappNumber: whatsappInput.trim(),
      supportPhone: whatsappInput.trim(),
      lastUpdatedBy: `${adminName} (${adminEmail})`,
      lastUpdatedAt: new Date().toISOString(),
    };
    saveContactConfig(updated);
    showToast(`🟢 Active WhatsApp number updated to ${whatsappInput.trim()} project-wide!`);
  };

  return (
    <div className="space-y-6 text-gray-800">
      {/* Header Bar */}
      <div className="bg-gradient-to-r from-emerald-950 via-slate-900 to-emerald-950 text-white p-5 rounded-2xl border border-amber-400/40 shadow-xl flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
        <div>
          <div className="flex items-center gap-2">
            <span className="text-2xl">📸</span>
            <div>
              <h3 className="text-lg font-serif font-black text-amber-300 uppercase tracking-wide">
                Multimedia & Asset Deployment Console
              </h3>
              <p className="text-xs text-emerald-200">
                One-Click Logo, Video, Banner & Image Deployer • Live App-Wide Sync
              </p>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <div className="bg-emerald-900/80 px-3 py-1.5 rounded-xl border border-amber-400/30 text-right">
            <span className="text-[10px] uppercase font-bold text-amber-300 block">Administrator Rights</span>
            <span className="text-xs font-black text-white">{adminName}</span>
            <span className="text-[10px] text-emerald-200 font-mono block">{adminEmail}</span>
          </div>

          <button
            onClick={handleResetAllMedia}
            className="px-3 py-2 bg-red-900/60 hover:bg-red-800 text-red-200 border border-red-500/50 rounded-xl text-xs font-bold transition-all cursor-pointer"
          >
            Reset Defaults
          </button>
        </div>
      </div>

      {/* Toast Notification */}
      {toastMessage && (
        <div className="bg-emerald-950 border-2 border-amber-400 text-amber-300 p-3 rounded-xl text-xs font-black flex items-center justify-between shadow-xl animate-fade-in">
          <span>{toastMessage}</span>
          <span className="text-emerald-400 font-mono text-[10px]">DEPLOYED LIVE</span>
        </div>
      )}

      {/* Active WhatsApp & Secretariat Contact Quick Control Banner */}
      <div className="bg-emerald-50 border-2 border-emerald-600 p-5 rounded-2xl shadow-sm flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <span className="text-3xl">🟢</span>
          <div>
            <h4 className="text-sm font-serif font-black text-emerald-950 uppercase">
              Active Kingdom WhatsApp Line: <span className="text-emerald-700 font-mono">{contactConfig.activeWhatsappNumber}</span>
            </h4>
            <p className="text-xs text-gray-600">
              Changeable by Administrator ({adminName}) • Deployed instantly across WhatsApp blasts, contact lists, footer, and support links.
            </p>
          </div>
        </div>

        <form onSubmit={handleSaveWhatsapp} className="flex items-center gap-2 w-full md:w-auto">
          <input
            type="text"
            value={whatsappInput}
            onChange={(e) => setWhatsappInput(e.target.value)}
            placeholder="+2348059079068"
            className="bg-white border border-emerald-300 rounded-xl px-3 py-2 text-xs font-mono font-bold text-gray-900 focus:ring-2 focus:ring-emerald-600 focus:outline-none flex-1 md:w-48"
          />
          <button
            type="submit"
            className="px-4 py-2 bg-emerald-700 hover:bg-emerald-800 text-white rounded-xl text-xs font-black uppercase tracking-wider transition-all cursor-pointer shadow shrink-0"
          >
            Update Active Line
          </button>
        </form>
      </div>

      {/* Main Grid: 3 Management Sections */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        
        {/* Section 1: Logo & Crest Deployer */}
        <div className="bg-white p-5 rounded-2xl border border-amber-200 shadow-xs space-y-4 flex flex-col justify-between">
          <div className="space-y-3">
            <div className="flex items-center justify-between border-b border-gray-100 pb-2">
              <h4 className="text-sm font-serif font-black text-emerald-950 uppercase flex items-center gap-1.5">
                <span>👑</span> Primary Royal Logo & Crest
              </h4>
              <span className="text-[9px] bg-amber-100 text-amber-900 font-bold px-2 py-0.5 rounded border border-amber-300">
                Logo Control
              </span>
            </div>

            {/* Active Logo Display */}
            <div className="flex items-center gap-3 bg-slate-50 p-3 rounded-xl border border-gray-200">
              <div className="w-14 h-14 rounded-full border-2 border-amber-400 p-0.5 bg-white shrink-0 overflow-hidden flex items-center justify-center">
                <img
                  src={media.customLogoUrl || 'https://res.cloudinary.com/uuovmrek/image/upload/uzairuelogo01'}
                  alt="Active Logo"
                  className="w-full h-full object-contain rounded-full"
                  referrerPolicy="no-referrer"
                />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs font-bold text-gray-900 truncate">
                  {media.customLogoUrl ? 'Custom Royal Crest Deployed' : 'Original Uzairue Palace Seal'}
                </p>
                <p className="text-[10px] text-gray-500 font-mono">App-Wide Dynamic Sync</p>
              </div>
            </div>

            <input
              type="file"
              ref={logoInputRef}
              accept="image/*"
              onChange={handleLogoUpload}
              className="hidden"
            />

            <button
              onClick={() => logoInputRef.current?.click()}
              className="w-full py-2.5 bg-emerald-800 hover:bg-emerald-900 text-amber-300 rounded-xl text-xs font-black uppercase tracking-wider transition-all cursor-pointer shadow-md flex items-center justify-center gap-2"
            >
              <span>⬆️</span> Upload & Deploy New Logo (1-Click)
            </button>

            {media.customLogoUrl && (
              <button
                onClick={() => {
                  deployMediaAsset('customLogoUrl', null, `${adminName} (${adminEmail})`);
                  showToast('Restored default royal seal logo.');
                }}
                className="w-full py-1 text-[10px] text-red-600 font-bold hover:underline cursor-pointer text-center"
              >
                Reset Logo to Default Seal
              </button>
            )}

            {/* Secondary Logo / Co-Sponsor Crest */}
            <div className="pt-3 border-t border-gray-100 space-y-2">
              <span className="text-[10px] font-bold text-gray-700 uppercase block">
                Secondary Co-Sponsor / Royal Crest:
              </span>
              <input
                type="file"
                ref={secondaryLogoInputRef}
                accept="image/*"
                onChange={handleSecondaryLogoUpload}
                className="hidden"
              />
              <div className="flex items-center justify-between bg-slate-50 p-2 rounded-xl border border-gray-200">
                <span className="text-xs text-gray-600 font-medium">
                  {media.secondaryLogoUrl ? 'Co-Sponsor Crest Active' : 'No Secondary Crest'}
                </span>
                <button
                  onClick={() => secondaryLogoInputRef.current?.click()}
                  className="px-2.5 py-1 bg-amber-600 hover:bg-amber-700 text-white rounded-lg text-[10px] font-bold cursor-pointer"
                >
                  Upload
                </button>
              </div>
            </div>
          </div>

          <div className="bg-amber-50 p-2.5 rounded-xl border border-amber-200 text-[10px] text-amber-900 font-medium">
            💡 Logos deployed here immediately update the Navbar, Footer, Publishing Suite Letterheads, Certificates, and Home Page.
          </div>
        </div>

        {/* Section 2: Video Broadcast & Media Stream Manager */}
        <div className="bg-white p-5 rounded-2xl border border-amber-200 shadow-xs space-y-4 lg:col-span-2 flex flex-col justify-between">
          <div className="space-y-3">
            <div className="flex items-center justify-between border-b border-gray-100 pb-2">
              <h4 className="text-sm font-serif font-black text-emerald-950 uppercase flex items-center gap-1.5">
                <span>🎬</span> Official Festival Video & Broadcast Stream
              </h4>
              <span className="text-[9px] bg-red-100 text-red-800 font-bold px-2 py-0.5 rounded border border-red-300">
                🔴 Video Deployer
              </span>
            </div>

            {/* Current Active Main Video Preview */}
            <div className="bg-slate-950 text-white p-3 rounded-xl border border-slate-800 space-y-2">
              <div className="flex items-center justify-between text-xs">
                <span className="font-bold text-amber-300 flex items-center gap-1">
                  <span>📺</span> Currently Deployed Main Video
                </span>
                <span className="bg-red-600 text-white text-[9px] font-extrabold px-2 py-0.5 rounded animate-pulse">
                  ACTIVE APP-WIDE
                </span>
              </div>

              {media.festivalVideoUrl && (
                <div className="aspect-video w-full rounded-lg overflow-hidden bg-black border border-slate-800">
                  {media.festivalVideoUrl.includes('youtube.com') || media.festivalVideoUrl.includes('youtu.be') ? (
                    <iframe
                      src={media.festivalVideoUrl}
                      title="Main Video"
                      className="w-full h-full"
                      allowFullScreen
                    />
                  ) : (
                    <video src={media.festivalVideoUrl} controls className="w-full h-full object-contain" />
                  )}
                </div>
              )}

              <div className="text-xs">
                <h5 className="font-bold text-amber-200">{media.festivalVideoTitle}</h5>
                <p className="text-[10px] text-slate-300 leading-tight">{media.festivalVideoDescription}</p>
              </div>
            </div>

            {/* Video Upload Controls */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 pt-2">
              {/* Option A: Local MP4/WebM Upload */}
              <div className="bg-emerald-50/60 p-3 rounded-xl border border-emerald-200 space-y-2">
                <span className="text-[10px] font-black uppercase text-emerald-900 block">
                  Option 1: Upload Local Video File (MP4 / WebM)
                </span>
                <input
                  type="file"
                  ref={localVideoInputRef}
                  accept="video/*"
                  onChange={handleLocalVideoUpload}
                  className="hidden"
                />
                <button
                  onClick={() => localVideoInputRef.current?.click()}
                  className="w-full py-2 bg-emerald-800 hover:bg-emerald-900 text-white rounded-lg text-xs font-bold transition-all cursor-pointer flex items-center justify-center gap-1.5"
                >
                  <span>📹</span> Select & Upload MP4 Video
                </button>
                <p className="text-[9px] text-gray-500">Converts and deploys directly to festival portal</p>
              </div>

              {/* Option B: Video Stream URL / Embed Link */}
              <form onSubmit={handleDeployVideoUrl} className="bg-amber-50/60 p-3 rounded-xl border border-amber-200 space-y-2">
                <span className="text-[10px] font-black uppercase text-amber-900 block">
                  Option 2: Embed Video URL / Live Stream
                </span>
                <input
                  type="text"
                  value={videoUrlInput}
                  onChange={(e) => setVideoUrlInput(e.target.value)}
                  placeholder="https://... (MP4 link, YouTube, or HLS stream)"
                  className="w-full bg-white border border-gray-300 rounded-lg p-1.5 text-xs focus:ring-2 focus:ring-amber-500 focus:outline-none"
                />
                <button
                  type="submit"
                  className="w-full py-2 bg-amber-600 hover:bg-amber-700 text-white rounded-lg text-xs font-bold transition-all cursor-pointer"
                >
                  Deploy Video URL →
                </button>
              </form>
            </div>

            {/* Video Metadata Optional Fields */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs">
              <input
                type="text"
                value={videoTitleInput}
                onChange={(e) => setVideoTitleInput(e.target.value)}
                placeholder="Video Title (e.g. Royal Procession 2026)"
                className="border border-gray-300 rounded-lg p-1.5 bg-white"
              />
              <input
                type="text"
                value={videoDescInput}
                onChange={(e) => setVideoDescInput(e.target.value)}
                placeholder="Video Description note..."
                className="border border-gray-300 rounded-lg p-1.5 bg-white"
              />
            </div>
          </div>
        </div>

      </div>

      {/* Row 2: Banners, Royal Portrait & Gallery Media Management */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        
        {/* Left Column: Hero Banners & Leadership Portrait */}
        <div className="lg:col-span-5 bg-white p-5 rounded-2xl border border-gray-200 shadow-xs space-y-4">
          <h4 className="text-sm font-serif font-black text-emerald-950 uppercase border-b border-gray-100 pb-2 flex items-center gap-1.5">
            <span>🌆</span> Banners & Executive Leadership Photos
          </h4>

          {/* Hero Background Banner */}
          <div className="space-y-2 bg-slate-50 p-3 rounded-xl border border-gray-200">
            <span className="text-xs font-bold text-gray-800 flex items-center justify-between">
              <span>🖼️ Main Hero Background Banner</span>
              <button
                onClick={() => heroBannerInputRef.current?.click()}
                className="text-[10px] bg-emerald-800 text-white font-bold px-2 py-0.5 rounded cursor-pointer"
              >
                Upload New
              </button>
            </span>
            <input
              type="file"
              ref={heroBannerInputRef}
              accept="image/*"
              onChange={handleHeroBannerUpload}
              className="hidden"
            />
            {media.heroBannerUrl && (
              <div className="h-20 rounded-lg overflow-hidden border border-gray-300 relative group">
                <img src={media.heroBannerUrl} alt="Hero Banner" className="w-full h-full object-cover" />
              </div>
            )}
          </div>

          {/* Executive Administrator / Royal Portrait */}
          <div className="space-y-2 bg-slate-50 p-3 rounded-xl border border-gray-200">
            <span className="text-xs font-bold text-gray-800 flex items-center justify-between">
              <span>👤 Administrator / Leadership Portrait</span>
              <button
                onClick={() => portraitInputRef.current?.click()}
                className="text-[10px] bg-amber-700 text-white font-bold px-2 py-0.5 rounded cursor-pointer"
              >
                Upload Photo
              </button>
            </span>
            <input
              type="file"
              ref={portraitInputRef}
              accept="image/*"
              onChange={handlePortraitUpload}
              className="hidden"
            />
            {media.royalPortraitUrl ? (
              <div className="flex items-center gap-3 bg-white p-2 rounded-lg border border-gray-200">
                <img src={media.royalPortraitUrl} alt="Leadership Portrait" className="w-12 h-12 object-cover rounded-full border border-amber-400" />
                <div className="text-xs">
                  <strong className="text-gray-900 block">{adminName}</strong>
                  <span className="text-[10px] text-gray-500">Super Administrator ({adminEmail})</span>
                </div>
              </div>
            ) : (
              <p className="text-[10px] text-gray-500 italic">No custom portrait uploaded yet.</p>
            )}
          </div>
        </div>

        {/* Right Column: Deployed Media Catalog & Gallery Manager */}
        <div className="lg:col-span-7 bg-white p-5 rounded-2xl border border-gray-200 shadow-xs space-y-4">
          <div className="flex items-center justify-between border-b border-gray-100 pb-2">
            <h4 className="text-sm font-serif font-black text-emerald-950 uppercase flex items-center gap-1.5">
              <span>📚</span> Deployed Media Asset Catalog
            </h4>

            {/* Gallery Upload Trigger */}
            <div className="flex items-center gap-2">
              <input
                type="text"
                value={photoTitleInput}
                onChange={(e) => setPhotoTitleInput(e.target.value)}
                placeholder="Image Title..."
                className="text-xs border border-gray-300 rounded-lg p-1 bg-white w-32"
              />
              <input
                type="file"
                ref={galleryPhotoInputRef}
                accept="image/*"
                onChange={handleGalleryPhotoUpload}
                className="hidden"
              />
              <button
                onClick={() => galleryPhotoInputRef.current?.click()}
                className="px-3 py-1 bg-emerald-800 hover:bg-emerald-900 text-white rounded-lg text-xs font-bold cursor-pointer"
              >
                + Add Photo
              </button>
            </div>
          </div>

          {/* Catalog Lists: Video Items */}
          <div className="space-y-2">
            <span className="text-[10px] font-black uppercase text-gray-500 tracking-wider block">
              Video Stream Resources ({media.promoVideos.length}):
            </span>
            <div className="space-y-1.5 max-h-36 overflow-y-auto pr-1">
              {media.promoVideos.map((vid) => {
                const isActive = media.festivalVideoUrl === vid.url;
                return (
                  <div key={vid.id} className="flex items-center justify-between bg-slate-50 p-2 rounded-xl border border-gray-200 text-xs">
                    <div className="flex items-center gap-2 min-w-0">
                      <span className="text-lg">🎬</span>
                      <div className="truncate">
                        <strong className="text-gray-900 block truncate">{vid.title}</strong>
                        <span className="text-[10px] text-gray-500">{vid.category || 'Video'} • {new Date(vid.updatedAt).toLocaleDateString()}</span>
                      </div>
                    </div>

                    <div className="flex items-center gap-1.5 shrink-0">
                      {isActive ? (
                        <span className="px-2 py-0.5 bg-emerald-100 text-emerald-900 text-[9px] font-black uppercase rounded">
                          ✓ Active Main
                        </span>
                      ) : (
                        <button
                          onClick={() => handleSetActiveVideo(vid)}
                          className="px-2 py-0.5 bg-amber-500 hover:bg-amber-400 text-slate-950 text-[9px] font-black uppercase rounded cursor-pointer"
                        >
                          Set Main
                        </button>
                      )}

                      <button
                        onClick={() => handleDeleteVideo(vid.id)}
                        className="text-red-500 hover:text-red-700 font-bold text-xs p-1"
                      >
                        ✕
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Catalog Lists: Gallery Photos */}
          <div className="space-y-2 pt-2 border-t border-gray-100">
            <span className="text-[10px] font-black uppercase text-gray-500 tracking-wider block">
              Gallery & Festival Photos ({media.galleryImages.length}):
            </span>
            <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 max-h-44 overflow-y-auto pr-1">
              {media.galleryImages.map((img) => (
                <div key={img.id} className="relative group rounded-lg overflow-hidden border border-gray-200 bg-slate-100 aspect-square">
                  <img src={img.url} alt={img.title} className="w-full h-full object-cover" />
                  <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-all p-1 flex flex-col justify-between text-white text-[9px]">
                    <span className="font-bold line-clamp-2">{img.title}</span>
                    <button
                      onClick={() => handleDeleteGalleryImage(img.id)}
                      className="bg-red-600 text-white font-bold py-0.5 rounded text-[8px] cursor-pointer text-center"
                    >
                      Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

      </div>
    </div>
  );
};

export default AdminMultimediaManager;
